<template>
  <div class="main-wrap">
    <main-header :name="$t('brand.name')" />
    <div class="container-wrap">
      <!-- Hero Section -->
      <div id="home">
        <banner-slider />
      </div>

      <!-- Services & Capabilities Section -->
      <div id="services" class="space-top-short space-bottom-short">
        <v-container>
          <feature />
        </v-container>
      </div>

      <div class="section-divider"></div>

      <!-- Counter Stats Section -->
      <div class="page-section">
        <counter dark :data="data" />
      </div>

      <div class="section-divider"></div>

      <!-- Process / Methodology Section -->
      <div id="process" class="space-top-short space-bottom-short">
        <v-container>
          <div class="text-center mb-8">
            <v-chip small outlined color="primary" class="process-chip mb-4">
              <v-icon small left>mdi-cog-sync-outline</v-icon>
              {{ $t('landing.processKicker') }}
            </v-chip>
            <h2 class="section-heading mb-3">{{ $t('landing.processTitle') }}</h2>
            <p class="section-subheading mb-0">{{ $t('landing.processSubtitle') }}</p>
          </div>

          <process-timeline
            :process-steps="processSteps"
            title=""
          />
        </v-container>
      </div>

      <div class="section-divider"></div>

      <!-- Portfolio / Case Studies Showcase -->
      <div id="portfolio" class="space-top-short space-bottom-short">
        <v-container>
          <showcase-apps
            :apps="featuredPortfolio"
            :title="$t('landing.portfolioTitle')"
            :subtitle="$t('landing.portfolioSubtitle')"
            :badge="$t('landing.portfolioBadge')"
          />
        </v-container>
      </div>

      <!-- Client Reviews / Testimonials -->
      <div id="testimonials">
        <Testimonials />
      </div>

      <div class="section-divider"></div>

      <!-- Tech Insights & Blog -->
      <div id="blog" class="space-top-short space-bottom-short">
        <blog />
      </div>

      <div class="section-divider"></div>

      <!-- Software Partnership / Enterprise Consulting Section -->
      <div id="developers" class="space-top-short space-bottom-short mx-2">
        <v-container>
          <v-card class="developer-card" elevation="0">
            <div class="developer-accent-strip"></div>
            <div class="developer-content text-center">
              <v-chip small outlined color="primary" class="developer-chip mb-4">
                <v-icon small left>mdi-handshake-outline</v-icon>
                {{ $t('landing.developerKicker') }}
              </v-chip>
              <h3 class="developer-title mb-3">{{ $t('landing.developerTitle') }}</h3>
              <p class="developer-subtitle mb-6">{{ $t('landing.developerSubtitle') }}</p>
              <p class="partner-note mb-6">{{ $t('landing.partnerNote') }}</p>
              <div class="developer-actions">
                <v-btn
                  large
                  color="primary"
                  :to="localePath('/contact')"
                  class="developer-partner-btn mx-2 mb-3"
                  elevation="4"
                >
                  <v-icon left>mdi-rocket-launch</v-icon>
                  {{ $t('landing.becomePartnerBtn') }}
                </v-btn>
                <v-btn
                  large
                  outlined
                  color="primary"
                  :to="localePath('/about')"
                  class="developer-docs-btn mx-2 mb-3"
                >
                  <v-icon left>mdi-information-outline</v-icon>
                  {{ $t('header.about') }}
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-container>
      </div>

      <div class="section-divider"></div>

      <!-- CTA / Contact Trigger -->
      <div id="contact">
        <call-action />
      </div>
    </div>
    <main-footer />
    <hidden point="smDown">
      <corner />
    </hidden>
  </div>
</template>

<style scoped lang="scss">
@import "~/assets/pages";

.section-heading {
  font-size: 2.2rem;
  font-weight: 800;
  line-height: 1.3;
  color: #1e293b;

  @media (max-width: 600px) {
    font-size: 1.6rem;
  }
}

.section-subheading {
  font-size: 1.1rem;
  color: #64748b;
  max-width: 650px;
  margin: 0 auto;
}

.process-chip {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.section-divider {
  width: 80%;
  max-width: 900px;
  height: 1px;
  margin: 0 auto;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(63, 81, 181, 0.18) 20%,
    rgba(244, 67, 54, 0.15) 80%,
    transparent 100%
  );
}

.developer-card {
  border-radius: 20px !important;
  background: linear-gradient(145deg, rgba(255,255,255,0.97), rgba(248,250,255,0.92)) !important;
  border: 1px solid rgba(63, 81, 181, 0.14) !important;
  box-shadow: 0 8px 28px rgba(36, 54, 128, 0.1) !important;
  overflow: hidden;
}

.developer-accent-strip {
  height: 4px;
  background: linear-gradient(90deg, #3f51b5 0%, #7c3aed 50%, #e53935 100%);
}

.developer-content {
  padding: 48px 32px;
}

.developer-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1a237e;

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
}

.developer-subtitle {
  font-size: 1.15rem;
  color: #475569;
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.6;
}

.partner-note {
  font-size: 1rem;
  color: #3f51b5;
  font-weight: 600;
}

.developer-actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
}

.developer-partner-btn {
  font-weight: 700;
  letter-spacing: 0.3px;
  border-radius: 12px;
  padding: 0 28px !important;
}

.developer-docs-btn {
  font-weight: 700;
  letter-spacing: 0.3px;
  border-radius: 12px;
  border-width: 2px !important;
  padding: 0 28px !important;
}
</style>

<script>
import Header from "~/components/Header";
import Hidden from "~/components/Hidden";
import BannerSlider from "~/components/hero";
import Feature from "~/components/Feature";
import Counter from "~/components/Counter";
import Testimonials from "~/components/Testimonials";
import Blog from "~/components/Blog";
import Footer from "~/components/Footer";
import Corner from "~/components/Corner";
import CallAction from "~/components/CallAction";
import ShowcaseApps from "~/components/Portfolio/ShowcaseApps";
import ProcessTimeline from "~/components/ProcessTimeline/ProcessTimeline";
import portfolioData from "~/data/portfolio.json";

export default {
  components: {
    "main-header": Header,
    BannerSlider,
    Hidden,
    Feature,
    Counter,
    ProcessTimeline,
    Testimonials,
    CallAction,
    Blog,
    Corner,
    "main-footer": Footer,
    ShowcaseApps
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    featuredPortfolio() {
      return portfolioData;
    },
    processSteps() {
      return [
        {
          title: this.$t('landing.step1Title'),
          dec: this.$t('landing.step1Desc'),
          icon: 'mdi-compass-outline',
          color: 'indigo',
          duration: this.$i18n.locale === 'ar' ? 'المرحلة الأولى' : 'Phase 1'
        },
        {
          title: this.$t('landing.step2Title'),
          dec: this.$t('landing.step2Desc'),
          icon: 'mdi-palette-outline',
          color: 'teal',
          duration: this.$i18n.locale === 'ar' ? 'المرحلة الثانية' : 'Phase 2'
        },
        {
          title: this.$t('landing.step3Title'),
          dec: this.$t('landing.step3Desc'),
          icon: 'mdi-xml',
          color: 'purple',
          duration: this.$i18n.locale === 'ar' ? 'المرحلة الثالثة' : 'Phase 3'
        },
        {
          title: this.$t('landing.step4Title'),
          dec: this.$t('landing.step4Desc'),
          icon: 'mdi-rocket-launch',
          color: 'red',
          duration: this.$i18n.locale === 'ar' ? 'المرحلة الرابعة' : 'Phase 4'
        }
      ];
    }
  },
  head() {
    const title = this.$t("seo.home.title");
    const desc = this.$t("seo.home.desc");
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Motanamy",
      "url": "https://www.motanamy.com",
      "logo": "https://www.motanamy.com/images/logo.png",
      "description": desc,
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "availableLanguage": ["Arabic", "English"]
      }
    };
    return {
      title,
      meta: [
        { hid: "description", name: "description", content: desc },
        { hid: "og:title", property: "og:title", content: title },
        { hid: "og:description", property: "og:description", content: desc },
        { hid: "og:url", property: "og:url", content: `https://www.motanamy.com${this.$route.path}` },
        { hid: "twitter:title", name: "twitter:title", content: title },
        { hid: "twitter:description", name: "twitter:description", content: desc },
      ],
      __dangerouslyDisableSanitizers: ["script"],
      script: [
        {
          hid: "schema-org",
          type: "application/ld+json",
          innerHTML: JSON.stringify(organizationSchema),
        },
      ],
    };
  },
  data: () => ({
    data: [
      {
        icon: "mdi-rocket-launch-outline",
        value: 25
      },
      {
        icon: "mdi-account-group-outline",
        value: 15
      },
      {
        icon: "mdi-server-network",
        value: 40
      },
      {
        icon: "mdi-clock-check-outline",
        value: 24
      }
    ]
  })
};
</script>
